/**
 * Marco Polo Section Component
 * 
 * HNS-specific component for displaying and managing Marco Polo events.
 * Shows active events, player responses, and penalty information.
 */

import { useState } from 'react';
import {
  MarcoPoloEvent,
  Player,
  MARCO_POLO_GRACE_PERIOD_SECONDS,
} from '@swtorbit/shared-socket-events';

interface MarcoPoloSectionProps {
  events: MarcoPoloEvent[];
  gamePlayers: Player[];
  onCancel: (eventId: string) => void;
}

export function MarcoPoloSection({ events, gamePlayers, onCancel }: MarcoPoloSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Get player display name
  const getPlayerName = (playerId: string) => {
    const player = gamePlayers.find(p => p.playerId === playerId);
    return player?.characterName || `Player ${playerId.slice(-4)}`;
  };
  
  // Format timestamp
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };
  
  // Calculate penalty time for a player who hasn't responded
  const getPenaltyInfo = (event: MarcoPoloEvent, playerId: string) => {
    const response = event.responses.find(r => r.playerId === playerId);
    if (!response) {
      const elapsed = Date.now() - event.activatedAt;
      const gracePeriodMs = MARCO_POLO_GRACE_PERIOD_SECONDS * 1000;
      if (elapsed > gracePeriodMs) {
        const penaltySecs = Math.floor((elapsed - gracePeriodMs) / 1000);
        return { status: 'penalizing' as const, penalty: penaltySecs };
      }
      return { status: 'waiting' as const, penalty: 0 };
    }
    return { status: 'complied' as const, penalty: response.penaltySeconds };
  };
  
  // Check if event is cancelled
  const isCancelled = (event: MarcoPoloEvent) => event.status === 'cancelled';
  
  return (
    <div className="border-top border-navy pt-sm mt-sm">
      <div 
        className="d-flex align-items-center gap-xs p-xs rounded-xs"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="marco-polo-toggle">{isExpanded ? '▼' : '▶'}</span>
        <span className="marco-polo-title">
          📍 Marco Polo Events ({events.length})
        </span>
      </div>
      
      {isExpanded && (
        <div className="marco-polo-events">
          {events.map(event => (
            <div key={event.id} className={`card card--sm card--bg-midnight card--border card--border-navy border-start border-xl border-pink-dark${isCancelled(event) ? ' o-60' : ''}`}>
              <div className="event-header">
                <span className="event-time">{formatTime(event.activatedAt)}</span>
                {!isCancelled(event) && event.status === 'active' && (
                  <button 
                    className="btn btn--ghost btn--sm btn-cancel"
                    onClick={() => onCancel(event.id)}
                  >
                    Cancel
                  </button>
                )}
                {isCancelled(event) && (
                  <span className="event-cancelled-badge">Cancelled</span>
                )}
                {event.status === 'completed' && (
                  <span className="event-completed-badge">Completed</span>
                )}
              </div>
              
              <div className="event-responses">
                {event.responses.map(response => (
                  <div key={response.playerId} className="d-flex align-items-center gap-sm px-xs py-2xs rounded-2xs fs-sm">
                    <span className="response-player">{getPlayerName(response.playerId)}</span>
                    <span className="response-coords">
                      ({Math.round(response.coordinates.x)}, {Math.round(response.coordinates.y)})
                    </span>
                    {response.penaltySeconds > 0 && (
                      <span className="response-penalty">
                        -{response.penaltySeconds}s penalty
                      </span>
                    )}
                  </div>
                ))}
                
                {/* Show hiders who haven't responded yet */}
                {event.status === 'active' && (event.targetPlayerIds || [])
                  .filter((id: string) => !event.responses.find(r => r.playerId === id))
                  .map((playerId: string) => {
                    const info = getPenaltyInfo(event, playerId);
                    return (
                      <div key={playerId} className={`d-flex align-items-center gap-sm px-xs py-2xs rounded-2xs fs-sm ${info.status === 'waiting' ? 'bg-amber bg-o-15' : 'bg-red bg-o-15 anim anim--flash anim--infinite'}`}>
                        <span className="response-player">{getPlayerName(playerId)}</span>
                        <span className="response-waiting">
                          {info.status === 'waiting' ? '⏳ Waiting...' : `🔴 Penalizing (-${info.penalty}s)`}
                        </span>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
